import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileSignature } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AuthImage } from "@/components/AuthImage";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtDate, fmtDateTime, FREQ_LABELS, FREQ_UNIT, LOAN_STATUS, loanTenor, rupiah } from "@/lib/format";

const Row = ({ k, v }) => <div className="flex justify-between gap-3 py-2 border-b text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v}</span></div>;

const Rows = ({ l }) => (
  <div>
    <Row k="No. Pinjaman · Tanggal" v={`${l.loan_no || "-"} · ${fmtDate(l.loan_date || l.created_at)}`} />
    <Row k="Anggota" v={`${l.member_name} (${l.nik})`} />
    <Row k="Status" v={LOAN_STATUS[l.status].label} />
    <Row k="Resort · Petugas" v={`${l.resort_name || "-"} · ${l.petugas_name || "-"}`} />
    <Row k="Pokok · Margin" v={`${rupiah(l.principal)} · ${l.margin_rate ? `${l.margin_rate}% flat` : `${l.interest_rate}%/bln`} (${rupiah(l.interest_total)})`} />
    <Row k="Tenor" v={`${loanTenor(l)} ${FREQ_UNIT[l.frequency]} · ${FREQ_LABELS[l.frequency]}`} />
    <Row k="Angsuran" v={rupiah(l.installment_amount)} />
    {l.admin_fee !== undefined && <Row k="Biaya Admin · Simpanan" v={`${rupiah(l.admin_fee)} · ${rupiah(l.savings_cut)}`} />}
    {l.net_disbursed !== undefined && <Row k="Diterima Anggota" v={rupiah(l.net_disbursed)} />}
    <Row k="Total · Terbayar" v={`${rupiah(l.total_payable)} · ${rupiah(l.paid_amount)}`} />
    {l.collateral_type && <Row k="Jaminan" v={l.collateral_type} />}
  </div>
);

const InstallmentTable = ({ l }) => {
  const rows = [...l.payments].reverse();
  if (!rows.length) return <p className="text-sm text-muted-foreground py-6 text-center" data-testid="loan-payments-empty">Belum ada pembayaran angsuran.</p>;
  const ratio = 1 + (l.margin_rate ?? 20) / 100;
  const tot = (f) => rows.reduce((s, p) => s + f(p), 0);
  const pokok = (p) => Math.round(p.amount / ratio);
  return (
    <div className="overflow-x-auto" data-testid="loan-payments">
      <Table>
        <TableHeader><TableRow><TableHead>Tanggal</TableHead><TableHead>Anggota</TableHead><TableHead className="text-right">Jumlah Pinjaman</TableHead><TableHead className="text-right">Margin 20%</TableHead><TableHead className="text-right">Nominal Angsuran</TableHead><TableHead className="text-right">Pokok Angsuran</TableHead><TableHead className="text-right">Margin Angsuran</TableHead><TableHead className="text-right">Sisa Pinjaman</TableHead></TableRow></TableHeader>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id}>
              <TableCell className="text-sm whitespace-nowrap">{fmtDate(p.created_at)}</TableCell>
              <TableCell className="text-sm">{p.member_name}</TableCell>
              <TableCell className="text-right font-mono">{rupiah(l.principal)}</TableCell>
              <TableCell className="text-right font-mono">{rupiah(l.interest_total)}</TableCell>
              <TableCell className="text-right font-mono font-semibold">{rupiah(p.amount)}</TableCell>
              <TableCell className="text-right font-mono">{rupiah(pokok(p))}</TableCell>
              <TableCell className="text-right font-mono">{rupiah(p.amount - pokok(p))}</TableCell>
              <TableCell className="text-right font-mono">{rupiah(p.remaining_after)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter><TableRow className="font-semibold"><TableCell colSpan={4}>Jumlah</TableCell><TableCell className="text-right font-mono">{rupiah(tot((p) => p.amount))}</TableCell><TableCell className="text-right font-mono">{rupiah(tot(pokok))}</TableCell><TableCell className="text-right font-mono">{rupiah(tot((p) => p.amount - pokok(p)))}</TableCell><TableCell /></TableRow></TableFooter>
      </Table>
    </div>
  );
};

export const LoanDetail = ({ id, onClose }) => {
  const [l, setL] = useState(null);
  useEffect(() => { if (!id) { setL(null); return; } api.get(`/loans/${id}`).then((r) => setL(r.data)).catch((e) => toast.error(errMsg(e))); }, [id]);
  return (
    <Dialog open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-5xl max-h-[92vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0" data-testid="loan-detail-dialog">
        <DialogHeader><DialogTitle className="font-heading">Rincian Pinjaman</DialogTitle></DialogHeader>
        {l && <Button asChild variant="outline" className="rounded-full h-10 w-fit"><Link to={`/pinjaman/${l.id}/kontrak`} onClick={onClose} data-testid="loan-view-contract"><FileSignature className="h-4 w-4 mr-1.5" />Lihat Kontrak</Link></Button>}
        {!l ? <p className="text-sm text-muted-foreground">Memuat...</p> : (
          <div className="space-y-5">
            <div className="grid sm:grid-cols-[140px_1fr] gap-5">
              <div className="space-y-3">
                {l.photo_path ? <AuthImage path={`/loans/${l.id}/photo`} alt={l.member_name} className="h-36 w-full sm:w-36 rounded-2xl object-cover" testId="loan-detail-photo" /> : <div className="h-36 rounded-2xl bg-muted grid place-items-center text-xs text-muted-foreground">Belum ada foto</div>}
                {l.collateral_path && <AuthImage path={`/loans/${l.id}/collateral`} alt="Jaminan" className="h-28 w-full sm:w-36 rounded-2xl object-cover" testId="loan-detail-collateral" />}
                {l.signature_path && <div><p className="text-[11px] text-muted-foreground mb-1">Tanda tangan anggota</p><AuthImage path={`/loans/${l.id}/signature`} alt="Tanda tangan" className="h-20 w-full sm:w-36 rounded-xl object-contain bg-white border" testId="loan-detail-signature" /></div>}
              </div>
              <Rows l={l} />
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Diajukan {fmtDateTime(l.created_at)} oleh {l.created_by_name}</p>
              {l.approved_at && <p>{l.status === "ditolak" ? "Ditolak" : "Disetujui"} {fmtDateTime(l.approved_at)} oleh {l.approved_by_name}</p>}
              {l.photo_at && <p>Foto diambil {fmtDateTime(l.photo_at)} oleh {l.photo_by_name}</p>}
              {l.collateral_at && <p>Jaminan diunggah {fmtDateTime(l.collateral_at)} oleh {l.collateral_by_name}</p>}
              {l.disbursed_at && <p>Dicairkan {fmtDateTime(l.disbursed_at)} oleh {l.disbursed_by_name}</p>}
            </div>
            <div><p className="font-heading font-semibold mb-2">Rincian Angsuran</p><InstallmentTable l={l} /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
